import { useEffect, useState } from 'react';
import { useAuth } from '../../../auth/AuthContext';
import { CountBadge } from '../../../components/CountBadge';
import type { PageTabs } from '../../../components/index-page/types';
import type { FacetValue } from '../../administration/users/userTypes';
import { issuancesApi } from './issuanceApi';
import { issuanceRequestsApi } from './issuanceRequestApi';

const REQUESTS = 'requests';
const ALL = 'all';

/** What the Issuances page reads off the location to land on one stage tab. */
export interface IssuancesLocationState {
  stage?: string;
}

/**
 * The two counts a page can NOT read off its own list (03 Nov 2026: every folder tab has a
 * badge) — the Requests waiting, and, on the Requests page, the register's stage buckets.
 *
 * Each is ONE page-of-one fetch, and each only for a reader holding the key behind it: no badge
 * is a count of rows the reader could not open. Both count LIVE rows, the lists' resting state.
 */
export function useIssuanceStripCounts(needStages: boolean): {
  requests: number | undefined;
  stages: FacetValue[] | undefined;
} {
  const { hasPermission } = useAuth();
  const canRequests = hasPermission('issuances.read-requests');
  const canIssuances = hasPermission('issuances.read');

  const [requests, setRequests] = useState<number | undefined>(undefined);
  const [stages, setStages] = useState<FacetValue[] | undefined>(undefined);

  useEffect(() => {
    if (!canRequests) return;
    const controller = new AbortController();

    issuanceRequestsApi
      .list({ page: 1, pageSize: 1, status: 'active' }, controller.signal)
      .then((result) => setRequests(result.total))
      .catch(() => undefined);

    return () => controller.abort();
  }, [canRequests]);

  useEffect(() => {
    if (!needStages || !canIssuances) return;
    const controller = new AbortController();

    issuancesApi
      .list({ page: 1, pageSize: 1, status: 'active' }, controller.signal)
      .then((result) => setStages(result.facets.stage ?? []))
      .catch(() => undefined);

    return () => controller.abort();
  }, [needStages, canIssuances]);

  return { requests, stages };
}

const requestsTab = (count: number | undefined) => ({
  key: REQUESTS,
  label: (
    <>
      Requests
      <CountBadge count={count} tone="slate" />
    </>
  ),
  permission: 'issuances.read-requests',
});

/**
 * Issuances' folder strip — **All · Requests · Issued · Partially Returned · Returned**
 * (product call, 03 Nov 2026). Requests is a separate route and key, drawn after All.
 */
export function issuancesPageTabs(navigate: (to: string) => void, requests: number | undefined): PageTabs {
  return {
    tabs: [requestsTab(requests)],
    afterAll: true,
    onChange: (key) => {
      if (key === REQUESTS) navigate('/inventory/issuances/requests');
    },
  };
}

/**
 * The SAME strip as the Requests page draws it — Requests active, the register's All and stage
 * buckets around it, each going back to Issuances on that tab. The bucket values and labels are
 * the SERVER's (the stage facet), so the two pages cannot spell a tab differently; until they
 * arrive the strip draws Requests alone.
 */
export function requestsPageTabs(
  navigate: (to: string, options?: { state?: IssuancesLocationState }) => void,
  requests: number | undefined,
  stages: FacetValue[] | undefined,
): PageTabs {
  return {
    tabs: [
      ...(stages
        ? [
            {
              key: ALL,
              label: (
                <>
                  All
                  {/* The buckets PARTITION the register, so their sum is its total. */}
                  <CountBadge count={stages.reduce((total, s) => total + s.count, 0)} tone="slate" />
                </>
              ),
              permission: 'issuances.read',
            },
          ]
        : []),
      requestsTab(requests),
      ...(stages ?? []).map((stage) => ({
        key: stage.value,
        label: (
          <>
            {stage.label}
            <CountBadge count={stage.count} tone="slate" />
          </>
        ),
        permission: 'issuances.read',
      })),
    ],
    activeKey: REQUESTS,
    onChange: (key) => {
      if (key === REQUESTS) return;
      navigate('/inventory/issuances', { state: key === ALL ? {} : { stage: key } });
    },
  };
}
